import { create } from 'zustand'
import type { Notification } from '@/types/notification'
import {
  subscribeToNotifications,
  markAsRead as markAsReadApi,
  markAllAsRead as markAllAsReadApi,
} from '@/services/api/notifications'
import type { Unsubscribe } from 'firebase/firestore'

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isOpen: boolean
  isLoading: boolean

  // Subscription management
  subscribe: (userId: string) => void
  unsubscribe: () => void

  // Actions
  markAsRead: (id: string) => void
  markAllAsRead: (userId: string) => void
  toggle: () => void
  close: () => void
}

let _unsubscribe: Unsubscribe | null = null

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  isLoading: true,

  subscribe: (userId: string) => {
    // Clean up existing subscription first
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }

    set({ isLoading: true })

    _unsubscribe = subscribeToNotifications(
      userId,
      (notifications) => {
        const unreadCount = notifications.filter((n) => !n.read).length
        set({ notifications, unreadCount, isLoading: false })
      },
      () => {
        set({ notifications: [], unreadCount: 0, isLoading: false })
      }
    )
  },

  unsubscribe: () => {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    set({ notifications: [], unreadCount: 0, isOpen: false, isLoading: false })
  },

  markAsRead: async (id: string) => {
    try {
      await markAsReadApi(id)
      // Optimistic update (real-time listener will also update)
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  },

  markAllAsRead: async (userId: string) => {
    try {
      await markAllAsReadApi(userId)
      // Optimistic update
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      }))
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err)
    }
  },

  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  close: () => set({ isOpen: false }),
}))
